import { TodoDto, TodoTypeDto } from '@act/shared/data-transfer-objects';
import {
  TodoDeletedEventPayload,
  TodoEventPayload,
  TodoEventTypes,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { createSelector } from 'reselect';
import { todoRequests } from '../requests/todos.requests';
import { useAppSelector } from '../store-hooks';
import { TodoState } from '../types/todoTypes';
import { createApiThunk } from '../utils/api-thunk';
import { newMembersLoaded } from './memberSlice';
import type { WebsocketEventActions } from '../types/websocket-types';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getTodos = createApiThunk(todoRequests.getTodos);
export const snoozeTodo = createApiThunk(todoRequests.snoozeTodo);

// INITIAL STATE ===============================================================
export const todoEntityAdapter = createEntityAdapter<TodoDto>();
const initialState: TodoState = {
  status: 'initial',
  loading: false,
  error: false,

  userId: null,
  todos: todoEntityAdapter.getInitialState(),
};

// SLICE =======================================================================
export const todoSlice = createSlice({
  name: 'todo',
  initialState,
  reducers: {
    todoCreated: (
      state: TodoState,
      action: PayloadAction<TodoEventPayload>
    ) => {
      const todo = action.payload.todo;
      if (todo.userId === state.userId) {
        return upsertTodoFromToday(todo, state);
      }
      return state;
    },

    todoDeleted: (
      state: TodoState,
      action: PayloadAction<TodoDeletedEventPayload>
    ) => {
      state.todos = todoEntityAdapter.removeOne(
        state.todos,
        action.payload.todoId
      );
      return state;
    },
    todoUpdated: (
      state: TodoState,
      action: PayloadAction<TodoEventPayload>
    ) => {
      const todo = action.payload.todo;
      if (todo.userId === state.userId) {
        return upsertTodoFromToday(todo, state);
      }
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })

      // Get Todos =====
      .addCase(getTodos.pending, state => {
        state.status = 'loading';
        todoEntityAdapter.removeAll(state.todos);
      })
      .addCase(getTodos.fulfilled, (state, action) => {
        state.status = 'success';
        state.userId = action.meta.arg.params.guideId;
        todoEntityAdapter.addMany(state.todos, action.payload.todos);
      })
      .addCase(getTodos.rejected, state => {
        state.status = 'failed';
      })

      // Snooze Todos =====
      .addCase(snoozeTodo.pending, state => {
        state.status = 'loading';
      })
      .addCase(snoozeTodo.fulfilled, (state, action) => {
        state.status = 'success';
        todoEntityAdapter.removeOne(state.todos, action.payload.id);
      })
      .addCase(snoozeTodo.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const todoReducer = todoSlice.reducer;

const upsertTodoFromToday = (todo: TodoDto, state: TodoState): TodoState => {
  const date = new Date();
  const todayDateString =
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    date.getDate().toString().padStart(2, '0');

  if (todo.date <= todayDateString) {
    // Upsert todo because it is in the right date range
    state.todos = todoEntityAdapter.upsertOne(state.todos, todo);
  } else {
    // Remove todo if it exits because it is out of the date range
    state.todos = todoEntityAdapter.removeOne(state.todos, todo.id);
  }

  return state;
};

// SELECTORS ===================================================================
interface TodosAppSelectorProps {
  type?: TodoTypeDto;
  [key: string]: unknown;
}

const selectTodosState = state =>
  (state as { todos: typeof initialState })?.todos?.todos;

const selectTodosSliceState = state =>
  (state as { todos: typeof initialState })?.todos;

const { selectAll: selectAllTodos, selectTotal: selectTodosTotal } =
  todoEntityAdapter.getSelectors(selectTodosState);

const selectIncompleteTodos = createSelector(selectAllTodos, todos =>
  todos.filter(todo => !todo.completedAt)
);

export const selectTodoSliceStatus = createSelector(
  selectTodosSliceState,
  todoState => todoState.status
);

const selectIncompleteTodoGroups = createSelector(
  selectIncompleteTodos,
  todos => {
    const groups = todos.reduce(
      (todoGroups, todo) => {
        if (todo.type === TodoTypeDto.SCHEDULED_EVENT) {
          const type = todo.scheduledEvent.type;
          if (type === 'call') {
            todoGroups.calls.push(todo);
          } else if (type === 'visit') {
            todoGroups.visits.push(todo);
          } else if (type === 'message') {
            todoGroups.messages.push(todo);
          } else {
            todoGroups.reminders.push(todo);
          }
        } else if (
          todo.type === TodoTypeDto.CALL_BACK ||
          todo.type === TodoTypeDto.FEEDBACK_CALL
        ) {
          todoGroups.calls.push(todo);
        } else if (todo.type === TodoTypeDto.TEXT_BACK) {
          todoGroups.messages.push(todo);
        } else {
          todoGroups.reminders.push(todo);
        }
        return todoGroups;
      },
      {
        calls: [] as TodoDto[],
        visits: [] as TodoDto[],
        messages: [] as TodoDto[],
        reminders: [] as TodoDto[],
      }
    );
    return groups;
  }
);

const selectReminderIncompleteTodoGroups = createSelector(
  selectIncompleteTodos,
  todos => {
    const groups = todos.reduce(
      (todoGroups, todo) => {
        if (todo.type === TodoTypeDto.SCHEDULED_EVENT) {
          const type = todo.scheduledEvent.type;
          if (type === 'reminder') {
            todoGroups.reminders.push(todo);
          }
        } else if (todo.type === TodoTypeDto.SCHEDULE_WORKFLOW_PHASE_REMINDER) {
          todoGroups.scheduleWorkflowPhaseReminders.push(todo);
        } else if (
          todo.type === TodoTypeDto.CALL_BACK ||
          todo.type === TodoTypeDto.FEEDBACK_CALL
        ) {
          todoGroups.calls.push(todo);
        } else if (todo.type === TodoTypeDto.TEXT_BACK) {
          todoGroups.messages.push(todo);
        } else {
          todoGroups.reminders.push(todo);
        }
        return todoGroups;
      },
      {
        calls: [] as TodoDto[],
        visits: [] as TodoDto[],
        messages: [] as TodoDto[],
        reminders: [] as TodoDto[],
        scheduleWorkflowPhaseReminders: [] as TodoDto[],
      }
    );
    return groups;
  }
);

export const selectIncompleteTodosByType: (
  state,
  props: TodosAppSelectorProps
) => TodoDto[] = createSelector(
  [selectIncompleteTodos, (state, props: TodosAppSelectorProps) => props.type],
  (todos, type) => todos.filter(todo => todo.type === type)
);

export const selectIncompleteTodosForFollowUp: (state, guideId) => TodoDto[] =
  createSelector(
    [selectIncompleteTodos, (state, guideId) => guideId],
    (todos, guideId) =>
      todos.filter(
        todo =>
          [
            TodoTypeDto.CALL_BACK,
            TodoTypeDto.FEEDBACK_CALL,
            TodoTypeDto.INCOMPLETE_CALL,
            TodoTypeDto.INCOMPLETE_NOTE_CALL,
            TodoTypeDto.INCOMPLETE_NOTE_VISIT,
            TodoTypeDto.INCOMPLETE_VISIT,
            TodoTypeDto.TEXT_BACK,
            TodoTypeDto.CALL_AUDIT,
          ].includes(todo.type) && todo.userId === guideId
      )
  );

// EXPORT HELPER ===============================================================
export const todosAppSelectors = {
  useSelectAllTodos: () => useAppSelector(selectAllTodos),
  useSelectSliceStatus: () => useAppSelector(selectTodoSliceStatus),
  useSelectTodosTotal: () => useAppSelector(selectTodosTotal),
  useSelectIncompleteTodoGroups: () =>
    useAppSelector(selectIncompleteTodoGroups),
  useSelectReminderIncompleteTodoGroups: () =>
    useAppSelector(selectReminderIncompleteTodoGroups),
  useSelectIncompleteTodosByType: (todoType: TodoTypeDto) =>
    useAppSelector(state =>
      selectIncompleteTodosByType(state, { type: todoType })
    ),
  useSelectIncompleteTodosForFollowUp: (guideId: string) =>
    useAppSelector(state => selectIncompleteTodosForFollowUp(state, guideId)),
};

// EFFECTS ================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: getTodos.fulfilled,
    effect: async ({ dispatch, action }) => {
      dispatch(newMembersLoaded({ members: action.payload.members }));
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const todoSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: TodoEventTypes.TODO_UPDATED,
    action: todoSlice.actions.todoUpdated,
    channel: 'baseSocket',
  },
  {
    type: TodoEventTypes.TODO_CREATED,
    action: todoSlice.actions.todoCreated,
    channel: 'baseSocket',
  },
  {
    type: TodoEventTypes.TODO_DELETED,
    action: todoSlice.actions.todoDeleted,
    channel: 'baseSocket',
  },
];
